import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ExplanationWithTimes, { formatDurationHuman } from '../components/utils/ExplanationWithTimes';
import { DishTypeBadge, NutriScoreDisplay } from '../components/RecipeDisplay';
import ServingsAdjuster from '../components/ServingsAdjuster';
import { Clock, ChefHat, Timer, Users, Utensils, Maximize2, X, ArrowLeft } from 'lucide-react';
import Seo from '../components/Seo';
var CDN = process.env.NODE_ENV==='development' ? 'https://cdn.j7lab.com/dev' : 'https://cdn.j7lab.com';
const API_BASE_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  process.env.REACT_APP_API_URL ||
  'https://food.api.j7lab.com';

const FALLBACK_IMAGE_URL = 'https://via.placeholder.com/1200x800?text=Recette';


const sanitizeImageBase = (value) => {
  if (!value) return null;
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const INGREDIENT_IMAGE_BASE = sanitizeImageBase(
  CDN
);

const extractStandardIngredientId = (ingredient) => {
  if (!ingredient) return null;
  const direct = ingredient.standardIngredient ?? ingredient.standardIngredientId;
  if (direct && typeof direct === 'string') return direct;
  if (direct && typeof direct === 'object' && direct._id) return direct._id;
  return null;
};

const buildIngredientImageUrl = (id, base) => {
  if (!id || !base) return null;
  return `${base}/ingredients/${id}.png`;
};

const getIngredientImageUrl = (ingredient) => {
  if (!ingredient) return null;

  const standardId = extractStandardIngredientId(ingredient);
  if (standardId) {
    const url = buildIngredientImageUrl(standardId, INGREDIENT_IMAGE_BASE);
    if (url) return url;
  }

  if (typeof ingredient._id === 'string' && ingredient._id.trim()) {
    const url = buildIngredientImageUrl(ingredient._id.trim(), INGREDIENT_IMAGE_BASE);
    if (url) return url;
  }

  if (typeof ingredient.imageUrl === 'string' && ingredient.imageUrl.trim()) {
    return ingredient.imageUrl;
  }

  if (typeof ingredient.image === 'string' && ingredient.image.trim()) {
    return ingredient.image;
  }

  return null;
};

const slugify = (value) => {
  if (!value) return '';
  return value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
};

const getIngredientPageUrl = (ingredient) => {
  if (!ingredient) return null;
  
  const standardId = extractStandardIngredientId(ingredient);
  if (!standardId) return null;
  
  // Récupère le slug depuis l'ingrédient ou génère un slug depuis le nom
  const slug = ingredient.standardIngredientSlug || 
               ingredient.slug || 
               (ingredient.standardIngredient && typeof ingredient.standardIngredient === 'object' && ingredient.standardIngredient.slug) ||
               slugify(ingredient.name);
  
  if (!slug) return null;
  
  return `/idees-de-repas/idees-recettes/${encodeURIComponent(slug)}/${encodeURIComponent(standardId)}`;
};

const pickHeroImage = (recipe) => {
  if (!recipe) return null;
  if (Array.isArray(recipe.imgUrlArray)) {
    const recipeImage = recipe.imgUrlArray.find(
      (url) => typeof url === 'string' && url.includes('/recipe')
    );
    if (recipeImage) return recipeImage;
    const firstValid = recipe.imgUrlArray.find((url) => typeof url === 'string' && url.trim().length > 0);
    if (firstValid) return firstValid;
  }
  if (typeof recipe.imageUrl === 'string' && recipe.imageUrl.trim().length > 0) {
    return recipe.imageUrl;
  }
  if (typeof recipe.heroImage === 'string' && recipe.heroImage.trim().length > 0) {
    return recipe.heroImage;
  }
  return null;
};

const groupIngredients = (ingredients) => {
  if (!Array.isArray(ingredients) || ingredients.length === 0) {
    return [];
  }

  const groups = new Map();

  ingredients.forEach((ingredient) => {
    const key = ingredient.section || ingredient.group || ingredient.category || 'Ingrédients';
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(ingredient);
  });

  return Array.from(groups.entries()).map(([title, items]) => ({ title, items }));
};

const adjustQuantityForServings = (originalQuantity, currentServings, baseServings) => {
  const numericQuantity = parseFloat(originalQuantity);

  if (!Number.isFinite(numericQuantity)) {
    return originalQuantity;
  }

  if (!Number.isFinite(currentServings) || !Number.isFinite(baseServings) || baseServings === 0) {
    return originalQuantity;
  }

  const adjustedQuantity = (numericQuantity * currentServings) / baseServings;

  if (adjustedQuantity === 0) {
    return 0;
  }

  if (adjustedQuantity < 1) {
    return Math.round(adjustedQuantity * 10) / 10;
  }

  if (adjustedQuantity < 10) {
    return Math.round(adjustedQuantity * 10) / 10;
  }

  return Math.round(adjustedQuantity);
};

const formatQuantity = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return String(value);
  }

  if (numeric === 0) {
    return '0';
  }

  if (Math.abs(numeric) >= 10) {
    return Math.round(numeric).toString();
  }

  if (Math.abs(numeric % 1) < 0.01) {
    return Math.round(numeric).toString();
  }

  return (Math.round(numeric * 10) / 10).toString();
};

const PublicRecipePage = () => {
  const { Recipe__url, Recipe___id } = useParams();
  const recipeSlug = Recipe__url;
  const recipeId = Recipe___id;
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [servings, setServings] = useState(null);
  const [isHeroImageOpen, setIsHeroImageOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [recipeId]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const fetchRecipe = async () => {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await fetch(`${API_BASE_URL}/api/public/recipe/${recipeId}`, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        const fetchedRecipe =
          payload?.data?.recipe ||
          payload?.recipe ||
          payload?.data ||
          payload;

        if (!fetchedRecipe) {
          throw new Error('Payload inattendu');
        }

        if (!isActive) return;

        setRecipe(fetchedRecipe);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Erreur lors du chargement de la recette publique:', error);
        if (!isActive) return;
        setRecipe(null);
        setErrorMessage('Impossible de charger cette recette. Veuillez réessayer plus tard.');
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchRecipe();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [recipeId]);

  useEffect(() => {
    if (!recipe?.url || !recipe?.id) return;
    if (recipeSlug !== recipe.url) {
      navigate(`/idees-de-repas/${recipe.url}/${recipe.id}`, { replace: true });
    }
  }, [recipe, recipeSlug, navigate]);

  useEffect(() => {
    if (!recipe) return;
    const baseServings = Number(recipe?.servings) || Number(recipe?.defaultServings) || Number(recipe?.people);
    const fallbackServings = Number.isFinite(baseServings) && baseServings > 0 ? baseServings : 2;
    setServings(fallbackServings);
  }, [recipe]);

  const heroImage = useMemo(() => pickHeroImage(recipe) || FALLBACK_IMAGE_URL, [recipe]);

  const metaTitle = recipe?.title ? `Idée recette YAQAM - ${recipe.title}` : 'Recette YAQAM';
  const metaDescription = recipe?.description
    ? recipe.description.slice(0, 155)
    : 'Découvrez une recette personnalisée générée par YAQAM.';

  const seo = (
    <Seo
      title={metaTitle}
      description={metaDescription}
      image={heroImage}
      breadcrumb
      type="article"
      noindex={!recipe}
      jsonLd={[
        {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "YAQAM",
          "url": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com',
          "logo": "https://www.yaqam.com/img/logo_color-min.png",
          "contactPoint": [{
            "@type": "ContactPoint",
            "contactType": "customer support",
            "email": "info@j7lab.com"
          }]
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Accueil",
              "item": (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com'
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Idées de recettes",
              "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + '/idees-de-repas'
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": 'Idée recette YAQAM - ' + (recipe?.title || 'Recette'),
              "item": ((typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : 'https://www.yaqam.com') + (typeof window !== 'undefined' ? window.location.pathname : '')
            }
          ]
        }
      ]}
    />
  );

  const originalServings = useMemo(() => {
    const baseServings = Number(recipe?.servings) || Number(recipe?.defaultServings) || Number(recipe?.people);
    if (Number.isFinite(baseServings) && baseServings > 0) {
      return baseServings;
    }
    return 2;
  }, [recipe]);

  const effectiveServings = useMemo(() => {
    const numericServings = Number(servings);
    if (Number.isFinite(numericServings) && numericServings > 0) {
      return numericServings;
    }
    return originalServings;
  }, [servings, originalServings]);

  const servingsDisplay = Number.isFinite(effectiveServings)
    ? (Number.isInteger(effectiveServings) ? `${effectiveServings}` : effectiveServings.toFixed(1))
    : null;

  const ingredientSections = useMemo(() => {
    const grouped = groupIngredients(recipe?.ingredients || []);
    if (grouped.length === 0) {
      return [{ title: 'Ingrédients', items: recipe?.ingredients || [] }];
    }
    return grouped;
  }, [recipe]);

  const orderedSteps = useMemo(() => {
    const normalizeStep = (rawStep, index) => {
      if (rawStep == null) {
        return null;
      }

      if (typeof rawStep === 'string') {
        const trimmed = rawStep.trim();
        if (!trimmed) {
          return null;
        }
        return {
          id: `step-${index}`,
          resolvedText: trimmed,
          ListOfTimingsInStep: [],
          order: index,
        };
      }

      const textCandidate =
        rawStep.step ||
        rawStep.description ||
        rawStep.text ||
        rawStep.instruction ||
        rawStep.content;

      const resolvedText = typeof textCandidate === 'string' ? textCandidate.trim() : '';

      if (!resolvedText) {
        return null;
      }

      const timings = Array.isArray(rawStep.ListOfTimingsInStep)
        ? rawStep.ListOfTimingsInStep
        : Array.isArray(rawStep.timings)
        ? rawStep.timings
        : [];

      return {
        ...rawStep,
        id: rawStep.id || rawStep._id || `step-${index}`,
        resolvedText,
        ListOfTimingsInStep: timings,
        order: Number(rawStep.order) || index,
      };
    };

    if (Array.isArray(recipe?.steps) && recipe.steps.length > 0) {
      return recipe.steps
        .map((step, index) => normalizeStep(step, index))
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);
    }

    if (Array.isArray(recipe?.instructions) && recipe.instructions.length > 0) {
      return recipe.instructions
        .map((step, index) => normalizeStep(step, index))
        .filter(Boolean)
        .sort((a, b) => a.order - b.order);
    }

    if (typeof recipe?.instructions === 'string' && recipe.instructions.trim()) {
      return recipe.instructions
        .split(/\r?\n+/)
        .map((line, index) => normalizeStep(line, index))
        .filter(Boolean);
    }

    return [];
  }, [recipe]);

  const prepTime = Number(recipe?.prepTime) || Number(recipe?.preparationTime) || null;
  const cookTime = Number(recipe?.cookTime) || Number(recipe?.bakingTime) || null;
  const totalTime = (prepTime || 0) + (cookTime || 0) || null;
  const servingsLabel = servingsDisplay
    ? `${servingsDisplay} part${Number(servingsDisplay) > 1 ? 's' : ''}`
    : null;

  const summaryItems = useMemo(() => {
    const items = [];
    if (prepTime != null) {
      items.push({ icon: Clock, label: 'Préparation', value: `${prepTime} min` });
    }
    if (cookTime != null) {
      items.push({ icon: Utensils, label: 'Cuisson', value: `${cookTime} min` });
    }
    if (totalTime && totalTime !== prepTime && totalTime !== cookTime) {
      items.push({ icon: Timer, label: 'Temps total', value: `${totalTime} min` });
    }
    if (servingsLabel) {
      items.push({ icon: Users, label: 'Portions', value: servingsLabel });
    }
    if (recipe?.complexity) {
      items.push({ icon: ChefHat, label: 'Difficulté', value: recipe.complexity });
    }
    return items;
  }, [prepTime, cookTime, totalTime, servingsLabel, recipe?.complexity]);

  if (isLoading) {
    return (
      <>
        {seo}
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-100">
          <div className="h-12 w-12 rounded-full border-4 border-orange-200 border-t-[#E67E22] animate-spin"></div>
        </div>
      </>
    );
  }

  if (errorMessage) {
    return (
      <>
        {seo}
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-100 px-4">
          <div className="max-w-lg bg-white border border-orange-200 rounded-2xl shadow-xl p-8 text-center">
            <h1 className="text-2xl font-semibold mb-4" style={{ color: '#2C3E50' }}>Recette indisponible</h1>
            <p className="text-sm" style={{ color: '#6c757d' }}>{errorMessage}</p>
          </div>
        </div>
      </>
    );
  }

  if (!recipe) {
    return seo;
  }

  return (
    <>
      {seo}
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-100 py-10 px-4">
      <div className="max-w-6xl mx-auto mb-6">
        <button
          onClick={() => navigate('/idees-de-repas')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-300 bg-white hover:bg-gray-50 transition-colors shadow-sm"
          style={{ color: '#2C3E50' }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Retour aux dernières idées de recettes</span>
        </button>
      </div>
      
      <article className="max-w-6xl mx-auto space-y-8">
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
          <header className="relative h-80 md:h-[420px]">
            <img
              src={heroImage}
              alt={recipe.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent"></div>
            <button
              type="button"
              onClick={() => setIsHeroImageOpen(true)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-lg"
              title="Voir l'image en plein écran"
              aria-label="Voir l'image en plein écran"
              style={{ color: '#2C3E50' }}
            >
              <Maximize2 className="w-5 h-5" />
            </button>
            <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 lg:p-12 text-white space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                {recipe.dishType && <DishTypeBadge type={recipe.dishType} />}
                {Array.isArray(recipe.cuisine) && recipe.cuisine.length > 0 && (
                  <span className="px-3 py-1 rounded-full text-xs uppercase tracking-wide bg-white/30 border border-white/40">
                    {recipe.cuisine.join(', ')}
                  </span>
                )}
              </div>
              <h1 className="text-3xl md:text-5xl font-bold drop-shadow-lg leading-tight">{recipe.title}</h1>
              {recipe.description && (
                <p className="text-sm md:text-base text-white/90 max-w-3xl">
                  {recipe.description}
                </p>
              )}
            </div>
          </header>

          <div className="p-6 md:p-10 lg:p-12">
            <div className="grid gap-10 lg:grid-cols-[2fr,1fr]">
              <div className="space-y-10">
                <ServingsAdjuster
                  servings={effectiveServings}
                  onServingsChange={setServings}
                  originalServings={originalServings}
                />

                <section className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 via-white to-gray-100/60 p-6 shadow-sm">
                  <h2 className="text-xl font-semibold mb-4" style={{ color: '#2C3E50' }}>Ingrédients</h2>
                  <div className="grid gap-4">
                    {ingredientSections.map((section, index) => (
                      <div key={`${section.title}-${index}`} className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 via-yellow-50 to-white p-5 shadow-sm">
                        {ingredientSections.length > 1 && (
                          <h3 className="text-base font-semibold mb-3" style={{ color: '#2C3E50' }}>{section.title}</h3>
                        )}
                        <ul className="space-y-2">
                          {section.items.map((ingredient, idx) => {
                            const adjustedQuantity = adjustQuantityForServings(
                              ingredient.quantity,
                              effectiveServings,
                              originalServings
                            );
                            const quantity = formatQuantity(adjustedQuantity);
                            const unity = ingredient.unity || ingredient.unit || '';
                            const ingredientImageUrl = getIngredientImageUrl(ingredient);
                            const ingredientPageUrl = getIngredientPageUrl(ingredient);
                            
                            return (
                              <li
                                key={`${ingredient.name}-${idx}`}
                                className="flex flex-wrap items-center justify-between gap-3 text-sm border-b border-orange-100 pb-2 last:border-b-0"
                                style={{ color: '#2C3E50' }}
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  {ingredientImageUrl && (
                                    <img
                                      src={ingredientImageUrl}
                                      alt={ingredient.name}
                                      className="h-10 w-10 rounded-full object-cover border border-orange-100 bg-white flex-shrink-0"
                                    />
                                  )}
                                  {ingredientPageUrl ? (
                                    <Link 
                                      to={ingredientPageUrl}
                                      className="font-medium truncate hover:opacity-80 transition-opacity"
                                      title={`Découvrir les bienfaits de ${ingredient.name}`}
                                      style={{ color: '#2C3E50' }}
                                    >
                                      {ingredient.name}
                                    </Link>
                                  ) : (
                                    <span className="font-medium truncate" title={ingredient.name}>{ingredient.name}</span>
                                  )}
                                </div>
                                <span className="text-sm whitespace-nowrap" style={{ color: '#6c757d' }}>
                                  {[quantity, unity].filter(Boolean).join(' ')}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="space-y-6">
                  <h2 className="text-2xl font-semibold" style={{ color: '#2C3E50' }}>Étapes de préparation de la recette</h2>
                  {orderedSteps.length > 0 ? (
                    <ol className="space-y-6 list-none">
                      {orderedSteps.map((step, index) => (
                        <li key={step.id || index} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4">
                          <div className="flex items-start gap-4">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center text-white text-lg font-semibold" style={{ backgroundColor: '#E67E22' }}>
                              {index + 1}
                            </div>
                            <div className="flex-1 space-y-3">
                              {step.resolvedText && (
                                <p className="text-sm md:text-base leading-relaxed" style={{ color: '#2C3E50' }}>
                                  <ExplanationWithTimes
                                    text={step.resolvedText}
                                    timings={step.ListOfTimingsInStep}
                                    servings={effectiveServings}
                                    originalServings={originalServings}
                                    ingredients={recipe.ingredients}
                                  />
                                </p>
                              )}
                              {Array.isArray(step.ListOfTimingsInStep) && step.ListOfTimingsInStep.length > 0 && (
                                <div className="text-xs" style={{ color: '#6c757d' }}>
                                  <span className="font-semibold">Temps associés :</span>
                                  <ul className="mt-1 space-y-1">
                                    {step.ListOfTimingsInStep.map((timing, timingIndex) => (
                                      <li key={timing.id || timingIndex}>
                                        {timing.label || 'Étape'} : {formatDurationHuman(timing.duration)}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {step.tips && (
                                <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm" style={{ color: '#6c757d' }}>
                                  💡 {step.tips}
                                </div>
                              )}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
                      <p className="text-sm" style={{ color: '#6c757d' }}>
                        Les étapes de préparation ne sont pas encore disponibles pour cette recette.
                      </p>
                    </div>
                  )}
                </section>
              </div>

              <aside className="space-y-6">
                {summaryItems.length > 0 && (
                  <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50 via-white to-gray-100/70 p-6 shadow-sm">
                    <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C3E50' }}>Informations clés</h2>
                    <ul className="space-y-3">
                      {summaryItems.map((item, index) => (
                        <li key={`${item.label}-${index}`} className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: '#EAEFF2', color: '#2C3E50' }}>
                              <item.icon className="w-5 h-5" />
                            </span>
                            <span className="text-sm font-medium" style={{ color: '#2C3E50' }}>{item.label}</span>
                          </div>
                          <span className="text-sm font-semibold" style={{ color: '#6c757d' }}>{item.value}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(recipe.nutriScore || recipe.nutriValues) && (
                  <div className="rounded-2xl overflow-hidden shadow-sm">
                    <NutriScoreDisplay
                      nutriScore={recipe.nutriScore}
                      nutriValues={recipe.nutriValues}
                      totalWeight={recipe.nutriValues?.totalWeight || 400}
                      servings={effectiveServings}
                      originalServings={originalServings}
                    />
                  </div>
                )}

                {/* Section "Pourquoi cette idée de recette va vous plaire" */}
                <section className="rounded-2xl border border-green-200 bg-gradient-to-br from-green-50 via-emerald-50 to-white p-6 shadow-sm">
                  <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C3E50' }}>
                    ✨ Pourquoi cette idée de recette a plu à l'utilisateur de YAQAM ?
                  </h2>
                  <div className="space-y-4">
                    {recipe?.benefits && recipe.benefits.length > 0 ? (
                      <ul className="space-y-3">
                        {recipe.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="text-green-500 text-lg mt-0.5">✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                            <strong>Facile à préparer :</strong> Une recette accessible qui ne demande pas de techniques compliquées
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-green-500 text-lg mt-0.5">✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                            <strong>Ingrédients disponibles :</strong> Tous les ingrédients se trouvent facilement en magasin
                          </span>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-green-500 text-lg mt-0.5">✓</span>
                          <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                            <strong>Goût garanti :</strong> Une combinaison de saveurs équilibrée qui plaira à toute la famille
                          </span>
                        </div>
                        {recipe?.healthBenefits && (
                          <div className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                              <strong>Bon pour la santé :</strong> {recipe.healthBenefits}
                            </span>
                          </div>
                        )}
                        {recipe?.complexity && typeof recipe.complexity === 'string' && recipe.complexity.toLowerCase().includes('facile') && (
                          <div className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                              <strong>Niveau débutant :</strong> Parfait pour se lancer en cuisine sans stress
                            </span>
                          </div>
                        )}
                        {(prepTime && prepTime <= 30) && (
                          <div className="flex items-start gap-3">
                            <span className="text-green-500 text-lg mt-0.5">✓</span>
                            <span className="text-sm leading-relaxed" style={{ color: '#2C3E50' }}>
                              <strong>Rapide à préparer :</strong> Seulement {prepTime} minutes de préparation
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </section>

                <section
                  className="relative rounded-2xl border p-6 shadow-sm overflow-hidden"
                  style={{
                    background: 'linear-gradient(145deg, rgba(236,240,244,0.85), rgba(206,214,224,0.95))',
                    borderColor: '#2C3E50'
                  }}
                >
                  <h2 className="text-lg font-semibold mb-4" style={{ color: '#2C3E50' }}>
                    🌟 Variantes et personnalisations de cette recette
                  </h2>
                  <div className="space-y-3 blur-sm select-none pointer-events-none opacity-80">
                    {(Array.isArray(recipe?.variantSuggestions) && recipe.variantSuggestions.length > 0
                      ? recipe.variantSuggestions.slice(0, 3)
                      : [
                          'Remplacez la protéine principale par une option végétarienne riche en saveurs.',
                          'Ajoutez un topping croustillant pour apporter du contraste.',
                          'Transformez cette recette en version express pour les soirs pressés.'
                        ]).map((item, index) => (
                          <div
                            key={index}
                            className="p-3 rounded-xl border text-sm leading-relaxed"
                            style={{ color: '#2C3E50', borderColor: '#2C3E50', backgroundColor: 'rgba(255,255,255,0.7)' }}
                          >
                            {typeof item === 'string' ? item : item?.description || item?.title || 'Suggestion exclusive'}
                          </div>
                        ))}
                  </div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                    <div
                      className="rounded-2xl backdrop-blur-md shadow-lg p-5 space-y-3"
                      style={{ backgroundColor: 'rgba(255,255,255,0.88)', border: '1px solid #2C3E50' }}
                    >
                      <p className="text-sm font-medium" style={{ color: '#2C3E50' }}>
                        Créez votre compte pour générer des variantes personnalisées et des suggestions exclusives.
                      </p>
                      <Link
                        to="/inscription"
                        className="inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold text-white"
                        style={{ backgroundColor: '#E67E22' }}
                      >
                        Je crée mon compte gratuitement
                      </Link>
                    </div>
                  </div>
                </section>

                {Array.isArray(recipe.tags) && recipe.tags.length > 0 && (
                  <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <h2 className="text-lg font-semibold mb-3" style={{ color: '#2C3E50' }}>Catégorie de repas</h2>
                    <div className="flex flex-wrap">
                          <ul className="flex gap-2 flex-wrap p-0 m-0 list-none">
                            {recipe.tags.map((tag) => (
                            <li key={tag} className="px-3 py-1 rounded-full text-sm bg-gray-100 border border-gray-200" style={{ color: '#6c757d' }}>
                              Recette {tag}
                            </li>
                          ))}
                          </ul>
                        </div>
                  </div>
                )}

                <div className="rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 via-amber-50 to-white p-6 shadow-sm text-center">
                  <h3 className="text-lg font-semibold" style={{ color: '#2C3E50' }}>Tu veux que YAQAM génère des recettes 100% adaptée à ton objectif et ton style de vie ?</h3>
                  <p className="text-sm mt-3" style={{ color: '#6c757d' }}>
                    Explorez toutes nos suggestions de repas personnalisées et trouvez votre prochaine inspiration.
                  </p>
                  <Link
                    to="/idees-de-repas"
                    className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold text-white transition-colors"
                    style={{ backgroundColor: '#E67E22' }}
                  >
                    Découvrir les recettes
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </article>
      {isHeroImageOpen && heroImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 px-4">
          <button
            type="button"
            onClick={() => setIsHeroImageOpen(false)}
            className="absolute top-6 right-6 p-2 rounded-full bg-white/90 hover:bg-white transition-colors shadow-lg"
            title="Fermer"
            aria-label="Fermer l'image plein écran"
            style={{ color: '#2C3E50' }}
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={heroImage}
            alt={recipe.title}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </div>
    </>
  );
};

export default PublicRecipePage;
